<template>
  <div>
    <v-navigation-drawer
      v-if="!isSmWidth"
      id="navigation-drawer-left"
      v-model="drawer"
      app
      temporary
      clipped
      :height="drawerHeight"
      :width="drawerWidth"
      :class="$style.drawer"
    >
      <v-list>
        <v-list-item>
          <v-list-item-content :class="$style['first-list-item']">
            <NavigationCustomerNumber
              :class="{
                [$style['customer-number']]: true,
                [$style['customer-number--support']]: isSupportUser,
              }"
            />
            <NavigationLanguageSwitch
              class="py-2 mr-2"
              :class="[$style['nav-button'], $style['language-switch']]"
            />
          </v-list-item-content>
        </v-list-item>
        <v-list-item
          v-for="(navigationItem, index) in navigationElements[navigationType]"
          :key="`tab-${navigationItem.id}-${index}`"
          :href="navigationItem.href"
          :to="navigationItem.to"
          :target="navigationItem?.attributes?.target"
          :class="[
            $style['list-item'],
            {
              [$style['list-item--active']]: activeListItem === index,
              [$style['list-item--current-route']]:
                $route.name === navigationItem.navigationRouteName,
              [$style.separator]: navigationItem.type === separatorType,
            },
            userAgentClass(navigationItem),
          ]"
          :ripple="false"
          :link="navigationItem.type !== separatorType"
          @mouseover="setNextListItem(index)"
          @mouseout="navigationItem.type === separatorType && setActiveListItem(-1)"
          @click="
            if ($route.name === navigationItem.navigationRouteName) {
              hideDrawer()
            }
          "
        >
          <v-list-item-content v-if="!navigationItem.hasChildren">
            <v-list-item-title>
              <NavigationElement v-bind="navigationItem" disabled />
            </v-list-item-title>
          </v-list-item-content>

          <v-menu
            v-else
            :id="`menu-${navigationItem.id}-${index}`"
            attach="#navigation-drawer-left"
            offset-x
            :close-delay="100"
            :value="activeListItem === index"
            :content-class="$style['submenu-wrapper']"
            :close-on-click="false"
            :close-on-content-click="false"
            @mouseover="cancelNextListItem"
          >
            <template #activator="{ on }">
              <v-list-item-content :class="$style['content-with-submenu']" v-on="on">
                <v-list-item-title>
                  <NavigationElement disabled v-bind="mergeItemConfigForSubnav(navigationItem)" />
                </v-list-item-title>

                <IconSelect :class="$style.arrow" icon="short-arrow-down" />
              </v-list-item-content>
            </template>

            <v-list :class="$style.submenu">
              <v-list-item
                v-for="submenuItem in navigationItem.children"
                :key="`submenu-${submenuItem.id}-${index}`"
                :href="submenuItem.href"
                :to="submenuItem.to"
                :class="[$style['submenu-item'], 'navigation-drawer__submenu']"
                :link="submenuItem.type !== separatorType"
                @mouseover="cancelNextListItem"
                @click="if ($route.name === submenuItem.navigationRouteName) hideDrawer()"
              >
                <v-list-item-content>
                  <v-list-item-title>
                    <NavigationElement v-bind="submenuItem" />
                  </v-list-item-title>
                </v-list-item-content>
              </v-list-item>
            </v-list>

            <Stripes :class="$style.stripes" />
          </v-menu>
        </v-list-item>

        <v-list-item :class="$style.separator">
          <v-list-item-content>
            <NavigationElement :id="`${imagesType}-separator`" :type="separatorType" disabled />
          </v-list-item-content>
        </v-list-item>

        <v-list-item>
          <v-list-item-content class="account" :class="$style['account']">
            <div :class="$style['account-buttons']">
              <NavigationLogoutItem class="py-3" :class="$style['logout-item']" />
              <NavigationImpersonationRelogin
                v-if="isSupportUser"
                class="py-3"
                :class="$style['logout-item']"
              />
              <NavigationStoreModeItem :class="$style['store-item']" class="py-3" />
            </div>
          </v-list-item-content>
        </v-list-item>

        <template v-if="navigationElements[imagesType].length > 0">
          <v-list-item :class="$style.separator" @mouseover="setActiveListItem(-1)">
            <v-list-item-content>
              <NavigationElement :id="`${imagesType}-separator`" :type="separatorType" disabled />
            </v-list-item-content>
          </v-list-item>

          <v-list-item @mouseover="setActiveListItem(-1)">
            <v-list-item-content>
              <div :class="$style.images">
                <NavigationElement
                  v-for="imageItem in navigationElements[imagesType]"
                  :key="imageItem.id"
                  v-bind="imageItem"
                />
              </div>
            </v-list-item-content>
          </v-list-item>
        </template>
      </v-list>
    </v-navigation-drawer>
    <MobileNavigation
      v-else-if="windowWith"
      :navigation-items="navigationElements.navigation"
      :is-support-user="!!isSupportUser"
    />
  </div>
</template>

<script>
import { get, throttle, cloneDeep } from 'lodash-es'
import { mapActions, mapGetters } from 'vuex'
import CONSTANTS from './utils/navigation-constants'

import { types as STOREMODULES } from '@/js/plugins/store/store-modules.js'
import APP_GETTERS from '@/js/plugins/store/modules/app/getter-types.js'
import APP_ACTIONS from '@/js/plugins/store/modules/app/action-types.js'
// import APP_CONSTANTS from '@/js/plugins/store/modules/app/constants.js'

import API_CONSTANTS from '@/js/helper/api-constants.js'

import { parseDataAttributeJson } from '@/js/helper/data-attribute-helper.js'
import { getAllNavigationItems } from '@/js/helper/navigation-helper.js'

import IconSelect from '@/js/components/Shared/IconSelect/IconSelect.vue'
import Stripes from '@/js/components/Shared/SvgSelect/SVG/DrawerStripes.vue'
import MessageCenterLinktilesMixin from '@/js/mixins/message-center-linktiles-mixin.js'
import PermissionMixin from '@/js/mixins/permission-mixin.js'
import ScopeMixin from '@/js/mixins/scope-mixin.js'

import NavigationElement from '@/js/components/Navigation/NavigationElement.vue'
import navigationTargets from '@/js/components/Navigation/components/navigation-targets'
import NavigationLogoutItem from '@/js/components/Navigation/NavigationLogoutItem.vue'
import NavigationStoreModeItem from '@/js/components/Navigation/NavigationStoreModeItem.vue'
import NavigationCustomerNumber from '@/js/components/Navigation/NavigationCustomerNumber.vue'
import NavigationLanguageSwitch from '@/js/components/Navigation/NavigationLanguageSwitch.vue'

import MobileNavigation from '@/js/components/Navigation/mobile/MobileNavigation.vue'

export default {
  name: 'NavigationDrawer',
  components: {
    IconSelect,
    Stripes,
    NavigationElement,
    MobileNavigation,
    NavigationLogoutItem,
    NavigationStoreModeItem,
    NavigationCustomerNumber,
    NavigationLanguageSwitch,
    NavigationImpersonationRelogin: () =>
      import(
        /* webpackChunkName: "NavigationImpersonationRelogin" */ '@/js/components/Navigation/NavigationImpersonationRelogin.vue'
      ),
  },
  mixins: [ScopeMixin, PermissionMixin, MessageCenterLinktilesMixin],
  props: {
    element: {
      type: String,
      default: '',
    },
  },
  // TODO: Update, make reactive when implementing mobile size
  data() {
    return {
      ...parseDataAttributeJson(this.element),
      topNavHeight: '5rem',
      drawerWidth: 480,
      activeListItem: -1,
      separatorType: API_CONSTANTS.NAVIGATION.SEPARATOR_TYPE,
      navigationType: API_CONSTANTS.NAVIGATION.NAVIGATION_TYPE,
      imagesType: API_CONSTANTS.NAVIGATION.IMAGES_TYPE,
      nextActiveListItem: -1,
      drawer: this.drawerIsVisible,
    }
  },
  computed: {
    /* Computed Attributes & Getters */
    // ...mapState(STOREMODULES.APP, {
    //   drawerState: APP_CONSTANTS.DRAWER_IS_VISIBLE,
    // }),
    ...mapGetters(STOREMODULES.APP, {
      drawerIsVisible: APP_GETTERS.NAVIGATION_DRAWER_VISIBILITY,
      windowWith: APP_GETTERS.GET_WIDTH,
      isSmWidth: APP_GETTERS.IS_SM_WIDTH,
      isSupportUser: APP_GETTERS.SUPPORT_USER,
    }),
    allNavigationItems() {
      const filterCallback = key => key === API_CONSTANTS.ELEMENTS

      return getAllNavigationItems(this.relationships, filterCallback)
    },
    navigationElements() {
      const navigationElements = this.allNavigationItems.reduce(
        (navigationElements, navigationItem) => {
          const type =
            navigationItem.type === this.imagesType ? this.imagesType : this.navigationType
          const processedNavigationItem = this.processNavigationItem(navigationItem)
          const children =
            navigationItem.attributes?.component === 'NavigationMessageCenter'
              ? this.generateMessageCenterLinkTiles()
              : get(
                  processedNavigationItem,
                  [API_CONSTANTS.RELATIONSHIPS, API_CONSTANTS.ELEMENTS, API_CONSTANTS.DATA],
                  [],
                )
                  .concat(get(processedNavigationItem, 'children_link_tiles', []))
                  .map(this.processNavigationItem)

          const icon = get(processedNavigationItem, [
            API_CONSTANTS.ATTRIBUTES,
            API_CONSTANTS.ICON.NAME,
          ])
          processedNavigationItem.hasChildren = !!children.length
          processedNavigationItem.hasChildren =
            icon === 'account' || icon === 'complaint' ? true : !!children.length
          processedNavigationItem.children = children.filter(navItem => navItem.show)
          processedNavigationItem.hasNoVisibleChildren =
            processedNavigationItem.hasChildren && !processedNavigationItem.children.length

          if (navigationItem.attributes?.component === 'NavigationMessageCenter') {
            navigationItem.show = !!children.length
            processedNavigationItem.hasNoVisibleChildren = !children.length
            navigationItem.children_link_tiles = navigationItem.children
          }

          if (processedNavigationItem.show && !processedNavigationItem.hasNoVisibleChildren) {
            navigationElements[type].push(processedNavigationItem)
          }
          return navigationElements
        },
        { [this.navigationType]: [], [this.imagesType]: [] },
      )
      return navigationElements
    },
    drawerHeight() {
      return `calc(100vh - ${this.topNavHeight})`
    },
  },
  watch: {
    drawerIsVisible() {
      this.drawer = this.drawerIsVisible
      this.activeListItem = -1
    },
    drawer() {
      if (this.drawer !== this.drawerIsVisible) {
        if (this.drawer) {
          this.showDrawer()
        } else {
          this.hideDrawer()
        }
      }
    },
  },

  methods: {
    ...mapActions(STOREMODULES.APP, {
      showDrawer: APP_ACTIONS.SHOW_NAVIGATION_DRAWER,
      hideDrawer: APP_ACTIONS.HIDE_NAVIGATION_DRAWER,
    }),
    setActiveListItem: throttle(
      function () {
        if (this.nextActiveListItem < 0) {
          // console.log(this.nextActiveListItem)
          return
        }
        this.activeListItem = this.nextActiveListItem
      },
      200,
      { leading: false },
    ),
    setNextListItem(index) {
      if (this.drawerIsVisible) {
        this.nextActiveListItem = index
        this.setActiveListItem()
      }
    },
    cancelNextListItem() {
      this.nextActiveListItem = this.activeListItem
    },
    getUrl(navigationItem) {
      return get(navigationItem, [API_CONSTANTS.ATTRIBUTES, API_CONSTANTS.URL], '')
    },
    userAgentClass(item) {
      let className = ''
      switch (item.attributes.user_agent_type) {
        case CONSTANTS.USER_AGENTS.MOBILE:
          className = 'mobile-only'
          break
        case CONSTANTS.USER_AGENTS.DESKTOP:
          className = 'desktop-only'
          break
        default:
          className = null
          break
      }
      return className
    },
    processNavigationItem(navigationItem) {
      const navigationComponent = get(navigationItem, [
        API_CONSTANTS.ATTRIBUTES,
        API_CONSTANTS.ICON.ATTRIBUTE.COMPONENT,
      ])
      const navigationRoute = navigationComponent
        ? get(navigationTargets, navigationComponent, 'default')
        : undefined
      const navigationRouteName = navigationRoute ? get(navigationRoute, 'name') : undefined
      const navigationRoutePerm = get(navigationRoute, 'perm')
      const navigationScopePerm = get(navigationRoute, 'scope')
      const navigationStoreCheck = get(navigationRoute, 'storeCheck')

      const show =
        (navigationStoreCheck ? navigationStoreCheck(this.$store) : true) &&
        (navigationRoutePerm ? this.hasRole(navigationRoutePerm) : true) &&
        (navigationScopePerm ? this.hasScope(navigationScopePerm) : true)
      const isSeparator = navigationItem.type === this.separatorType
      return {
        ...cloneDeep(navigationItem),
        href: isSeparator || navigationComponent ? undefined : this.getUrl(navigationItem),
        to: navigationRoute,
        show,
        navigationRouteName,
      }
    },
    mergeItemConfigForSubnav(navigationItem) {
      const shouldLinkToFirstChild = get(
        navigationItem,
        [API_CONSTANTS.ATTRIBUTES, API_CONSTANTS.NAVIGATION.ATTRIBUTE.AUTO_LINK_TO_FIRST_CHILD],
        0,
      )
      if (shouldLinkToFirstChild) {
        // const elements = get(
        //   navigationItem,
        //   [API_CONSTANTS.RELATIONSHIPS, API_CONSTANTS.ELEMENTS, API_CONSTANTS.DATA],
        //   [],
        // )
        const link = navigationItem.children.length
          ? get(navigationItem.children[0], [API_CONSTANTS.ATTRIBUTES, API_CONSTANTS.URL], '')
          : ''

        if (link) {
          navigationItem.href = link
        }
        navigationItem.disabled = link ? false : true
      }

      return navigationItem
    },
  },
}
</script>

<style lang="scss" module>
:global(.navigation-drawer--open .v-application .v-overlay) {
  top: 5rem !important;
}

.drawer {
  top: 5rem !important;

  overflow: visible;

  box-shadow: none !important;

  :global(.v-menu__content) {
    top: 0 !important;
    left: 100% !important;

    width: 30rem;

    height: 100%;
    overflow: scroll;

    background: var(--black);
    border-radius: 0;
    box-shadow: none !important;
  }

  :global(.v-list) {
    height: auto;
    padding-top: 3.125rem;
    overflow: hidden;
  }

  :global(.v-list) {
    display: flex;
    flex-direction: column;
  }

  :global(.v-list) {
    @supports (display: grid) {
      display: grid;
      grid-auto-rows: minmax(3.125rem, min-content);
      grid-row-gap: 0.5rem;
    }
  }

  :global(.v-list-item) {
    padding: 0 !important;

    text-decoration: none !important;

    cursor: pointer;

    &:last-child {
      margin-bottom: 3.125rem;
    }
  }

  :global(.v-list-item) {
    flex: 0 1 auto;
  }

  :global(.v-list-item__title) {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    margin-bottom: 0;

    text-decoration: none;

    transition: color 0.3s ease-in;

    > * {
      text-decoration: none;
    }
  }

  :global(.v-list-item__title),
  :global(.v-list-item__title) > span,
  :global(.v-list-item__title) > a,
  :global(.v-list-item__title) > p {
    padding: 0 !important;

    font-weight: var(--font-weight-light);
    font-size: 1.625rem;
    line-height: normal;

    white-space: normal;
    word-break: break-word;
    hyphens: auto;
  }

  :global(.v-list-item__content) {
    z-index: 1;

    padding: 0.625rem 3.75rem !important;
  }

  // Language switch and customer number wrapper styles

  .first-list-item {
    justify-content: space-between;

    .customer-number,
    .language-switch {
      flex: 0 0 0;
    }
  }

  .customer-number--support {
    padding: 5px 5px;

    color: var(--white);
    font-weight: var(--font-weight-bold);

    background: var(--red);

    svg {
      color: var(--white);
    }
  }
}

.list-item {
  &::before {
    right: -15%;
    z-index: 0;

    background: var(--white);

    transition: all 0.3s cubic-bezier(0.8, 0.25, 0.25, 0.8);
  }

  &:focus,
  &:hover,
  &--active,
  &--current-route {
    &::before {
      left: 1.75rem;

      transform: skewx(-20deg);
      opacity: 1 !important;
    }
  }

  &:hover,
  &--active {
    &::before {
      background: var(--black);
    }

    :global(.v-list-item__content) {
      * {
        color: var(--white) !important;
      }
    }
  }

  &--current-route {
    &::before {
      background: var(--primary) !important;
    }

    :global(.v-list-item__content) {
      * {
        color: var(--black) !important;
      }
    }
  }
}

.arrow {
  transform: rotate(-90deg);
}

.content-with-submenu {
  display: grid;
  grid-column-gap: 1rem;
  grid-template-rows: auto;
  grid-template-columns: 1fr min-content;
  align-items: center;

  :global(.v-list-item__title) {
    z-index: 2;
  }
}

.submenu-wrapper {
  overflow: hidden !important;
}

.submenu {
  height: 100% !important;
  overflow: scroll !important;

  color: var(--white) !important;

  background: transparent !important;

  :global(.v-list-item__content) {
    * {
      font-size: var(--font-2xl);
    }
  }

  :global(.v-list-item),
  :global(.v-list-item__title) {
    transition: color 0.3s ease;

    * {
      color: var(--white) !important;
      font-weight: var(--font-weight-medium) !important;
    }

    &:hover {
      color: var(--primary);
    }
  }
}

.separator {
  cursor: default !important;
  opacity: 1;
}

.stripes {
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  z-index: -1;

  width: 100%;
  height: 100%;
}

.images {
  display: grid;
  // TODO: Fix IE11 (Grid auto placement not working / minmax not working)
  grid-auto-rows: minmax(min-content, 5rem);
  grid-gap: 2rem 2.75rem;
  grid-template-rows: auto;
  grid-template-columns: repeat(3, 1fr);
  align-items: center;
}

.account {
  min-height: 110px;
}

.account-buttons {
  display: flex;
  flex-direction: column;
  justify-content: space-around;
  height: 100%;
  min-height: 1.75rem !important;
  margin: 0 10px;
}

.account-user {
  display: flex;
  flex-direction: column;
  justify-content: center;
  height: 100%;
  margin: 0 10px;
}

.store-item {
  :global(.v-input--selection-controls) {
    margin-top: 0;
  }
}

.logout-item,
.store-item {
  height: 2.5rem !important;
  margin-top: 5px;
  margin-bottom: 5px;
}
</style>
